import type { Me, ProjectListItem } from '@p2b/contract';
import { projectRoleRank } from '@p2b/contract';
import { projectState, type ProjectState } from '../orgParts.js';

// The home page's register without React or the API client, so tests can check it.

export type SortKey = 'name' | 'org' | 'latest' | 'status' | 'activity' | 'role';
export interface Sort {
  key: SortKey;
  /** 1: the natural order of the column (A→Ö, newest first); -1: reversed. */
  dir: 1 | -1;
}
/** Newest activity first: what someone opening the platform most likely wants. */
export const DEFAULT_SORT: Sort = { key: 'activity', dir: 1 };

const time = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : -Infinity);
const byName = (a: ProjectListItem, b: ProjectListItem) => a.name.localeCompare(b.name, 'is');

const compare: Record<SortKey, (a: ProjectListItem, b: ProjectListItem) => number> = {
  name: byName,
  org: (a, b) => a.orgName.localeCompare(b.orgName, 'is') || byName(a, b),
  // Newest delivery first; projects without one last.
  latest: (a, b) =>
    time(b.latestPublication?.publishedAt) - time(a.latestPublication?.publishedAt) || byName(a, b),
  status: (a, b) => projectState(a).localeCompare(projectState(b)) || byName(a, b),
  activity: (a, b) => time(b.lastActivityAt) - time(a.lastActivityAt) || byName(a, b),
  // Most powerful role first.
  role: (a, b) => rank(b) - rank(a) || byName(a, b),
};
const rank = (p: ProjectListItem) => (p.role ? projectRoleRank[p.role] : 0);

export function sortProjects(list: readonly ProjectListItem[], sort: Sort): ProjectListItem[] {
  return [...list].sort((a, b) => compare[sort.key](a, b) * sort.dir);
}

/** A click on a column: the same column flips its order, another starts in its natural order. */
export const nextSort = (cur: Sort, key: SortKey): Sort =>
  cur.key === key ? { key, dir: cur.dir === 1 ? -1 : 1 } : { key, dir: 1 };

export interface HomeFilter {
  q: string;
  /** Organization slug, or '' for all. */
  org: string;
  state: ProjectState | 'all';
}

export function filterHome(list: readonly ProjectListItem[], f: HomeFilter): ProjectListItem[] {
  const needle = f.q.trim().toLocaleLowerCase('is');
  return list.filter(
    (p) =>
      (!f.org || p.org === f.org) &&
      (f.state === 'all' || projectState(p) === f.state) &&
      (!needle ||
        [p.name, p.address, p.municipality, p.landNumber, p.slug, p.orgName, p.description].some((v) =>
          v?.toLocaleLowerCase('is').includes(needle),
        )),
  );
}

/** The organizations in the list, by name: the filter's choices. */
export function orgsOf(list: readonly ProjectListItem[]): { slug: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const p of list) seen.set(p.org, p.orgName);
  return [...seen].map(([slug, name]) => ({ slug, name })).sort((a, b) => a.name.localeCompare(b.name, 'is'));
}

/** One line of "Bíður þín". */
export type AttentionItem =
  | { kind: 'users'; n: number }
  | { kind: 'undelivered'; project: ProjectListItem; label: string; at: string }
  | { kind: 'inbox'; project: ProjectListItem; n: number };

/** What waits on the caller, most urgent kind first; empty when nothing does (the strip then hides). */
export function attentionItems(list: readonly ProjectListItem[], pendingUsers = 0): AttentionItem[] {
  const out: AttentionItem[] = [];
  if (pendingUsers > 0) out.push({ kind: 'users', n: pendingUsers });
  const newest = (a: ProjectListItem, b: ProjectListItem) => time(b.lastActivityAt) - time(a.lastActivityAt);
  for (const p of [...list].sort(newest)) {
    const u = p.attention?.undelivered;
    if (u) out.push({ kind: 'undelivered', project: p, label: u.label, at: u.createdAt });
  }
  for (const p of [...list].sort(newest)) {
    const n = p.attention?.inbox ?? 0;
    if (n > 0) out.push({ kind: 'inbox', project: p, n });
  }
  return out;
}

/**
 * Where "/" sends a person with exactly one project and nothing else to do here: straight to it (owner's proposal,
 * 2026-11-01). Only for clients and partners; staff keep the home page for "Bíður þín" and new projects.
 */
export function straightTo(me: Me | undefined, list: readonly ProjectListItem[] | undefined): string | null {
  if (!me || !list || list.length !== 1 || me.user.isSuperadmin) return null;
  if ((me.invitations?.length ?? 0) > 0) return null;
  const p = list[0]!;
  if (p.role !== 'client' && p.role !== 'partner') return null;
  return `/projects/${p.org}/${p.slug}`;
}

/** Whether the "Fyrirtæki" column earns its place: only when the list spans organizations. */
export const spansOrgs = (list: readonly ProjectListItem[]) => new Set(list.map((p) => p.org)).size > 1;
